import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/app/Header";
import { MoodTracker } from "../components/app/MoodTracker";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowUpRight, Heart } from "lucide-react";

const SIZE_MAP = {
  sick_parent: "md:col-span-2 md:row-span-2",
  new_baby: "md:col-span-1 md:row-span-1",
  career_decision: "md:col-span-1 md:row-span-2",
  financial_pressure: "md:col-span-2 md:row-span-1",
};

const Dashboard = () => {
  const [situations, setSituations] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/situations").then((r) => setSituations(r.data));
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bg)" }}>
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="mb-12 fade-in">
          <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: "var(--muted)" }}>
            Welcome back{user ? `, ${user.name.split(" ")[0]}` : ""}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-4" style={{ color: "var(--ink)" }}>
            What's weighing on you today?
          </h1>
          <p className="text-lg max-w-2xl" style={{ color: "var(--ink-soft)" }}>
            Pick a situation below. Each one opens into a quiet space with a guide, a plan, a
            journal, and resources — all yours.
          </p>
        </div>

        <div className="grid md:grid-cols-3 auto-rows-[180px] md:auto-rows-[220px] gap-5 stagger mb-12">
          {situations.map((s) => (
            <button
              key={s.key}
              onClick={() => navigate(`/situation/${s.key}`)}
              className={`relative overflow-hidden rounded-3xl border text-left group transition-all hover:-translate-y-1 hover:shadow-md ${SIZE_MAP[s.key] || ""}`}
              style={{ borderColor: "var(--border)", backgroundColor: "var(--surface)" }}
              data-testid={`situation-card-${s.key}`}
            >
              <div className="absolute inset-0">
                <img
                  src={s.image}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(42,52,43,0.1) 0%, rgba(42,52,43,0.7) 100%)",
                  }}
                />
              </div>
              <div className="relative h-full flex flex-col justify-end p-6 md:p-7 text-white">
                <p className="text-xs uppercase tracking-[0.2em] opacity-80 mb-2">
                  {s.subtitle}
                </p>
                <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl leading-tight mb-2">
                  {s.title}
                </h2>
                <div className="flex items-center gap-2 text-sm opacity-90">
                  Open space <ArrowUpRight className="w-4 h-4" />
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <MoodTracker />
          </div>
          <div
            className="rounded-3xl p-6 md:p-8 border relative overflow-hidden"
            style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border)" }}
            data-testid="resources-teaser"
          >
            <Heart className="w-5 h-5 mb-3" style={{ color: "var(--terracotta)" }} />
            <h3 className="font-serif text-2xl md:text-3xl mb-2" style={{ color: "var(--ink)" }}>
              Help, when you need it most.
            </h3>
            <p className="text-sm mb-5" style={{ color: "var(--ink-soft)" }}>
              A small, hand-picked directory of helplines, counselors, and guides.
            </p>
            <button
              onClick={() => navigate("/resources")}
              className="text-sm underline"
              style={{ color: "var(--terracotta)" }}
              data-testid="resources-link"
            >
              Open resources →
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
